import * as T from '@radix-ui/react-tooltip';
import {
  cloneElement,
  createContext,
  forwardRef,
  useContext,
  useState,
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../cn';
import { lastInputWasKeyboard } from './input-modality';

/** Плашка подсказки: лайм, как лаймовые кнопки, без обводки. Своя плашка слоя ударений просмотрщика — та же */
export const TOOLTIP_SURFACE =
  'nd:w-fit nd:max-w-xs nd:rounded-xl nd:bg-primary nd:px-3 nd:py-1.5 nd:text-xs nd:font-medium nd:break-words nd:text-primary-foreground nd:drop-shadow-[0_3px_6px_rgb(0_0_0/0.2)] nd:select-none';

/** Первая подсказка — через 700 мс (500 из концепта показались быстрыми); пока с ухода с прошлой не прошло 300 мс, следующая — сразу */
const OPEN_DELAY_MS = 700;
const SKIP_DELAY_MS = 300;

const InsideProvider = createContext(false);

/** Общий счётчик задержек для всего раздела: ставит NewDesignScope */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <T.Provider delayDuration={OPEN_DELAY_MS} skipDelayDuration={SKIP_DELAY_MS}>
      <InsideProvider.Provider value>{children}</InsideProvider.Provider>
    </T.Provider>
  );
}

/**
 * Safari сам показывает системную подсказку у текста, обрезанного многоточием (title не нужен, отключить нельзя) —
 * вторую поверх нашей. Не показывает, если после текста внутри обрезающего элемента стоит пустой блок: текст уходит
 * в анонимный блок, многоточие остаётся (проверено в Chrome). Ставит Tooltip с truncated; если обрезает не сам триггер,
 * а элемент внутри него (NameNote) — его кладут туда вручную.
 */
export function NoSafariTruncationTip() {
  return <span aria-hidden="true" className="nd:block" />;
}

/** Не помещается ли текст: сам элемент или его обрезающий потомок (truncate внутри тега) */
function isTruncated(el: Element): boolean {
  return [el, ...el.querySelectorAll('*')].some((n) => n.scrollWidth > n.clientWidth + 1);
}

export interface TooltipProps extends Omit<ComponentPropsWithoutRef<typeof T.Trigger>, 'children' | 'asChild'> {
  /** Текст подсказки; пусто — подсказка не всплывает (дерево то же: триггер не пересоздаётся, когда подсказка появляется) */
  label: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Только если триггер обрезан многоточием: у названий, которые обычно помещаются */
  truncated?: boolean;
  /** Триггер — один элемент, принимающий ref и обработчики (кнопка, span) */
  children: ReactElement;
}

/**
 * Подсказка нового дизайна (концепт tooltip-selection, 1.1 со стрелкой, без обводки) — tooltip shadcn из пресета
 * (audiobooks/web/src/components/ui/tooltip.tsx): шрифт, скругление, поля. Отличия: лаймовая, как лаймовые кнопки
 * (primary / primary-foreground), без обводки; стрелка — треугольник 14×7 целиком снаружи (ромбик shadcn торчал на 5 px,
 * лаймовый его было не видно); тень — drop-shadow на всё вместе, у box-shadow стрелка оставалась без тени.
 * Не всплывает от касания (Radix) и прячется при нажатии. С клавиатуры — по фокусу; фокус после мыши — без подсказки.
 * На выключенную кнопку не навести — подсказку вешают на обёртку.
 * Сам может быть ребёнком asChild-триггера (ConfirmPopoverTrigger): его пропсы и ref уходят на кнопку, и data-state
 * у неё — от вопроса, а не от подсказки.
 */
export const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(function Tooltip(
  { label, side = 'top', truncated = false, children, ...triggerProps },
  ref
) {
  const [open, setOpen] = useState(false);
  const empty = label == null || label === '';
  const blocked = (el: Element | null) => empty || (truncated && !!el && !isTruncated(el));
  /*
   * Ненужную подсказку не даём Radix даже запросить: запрос открытия сразу сбрасывает общую задержку (provider.onOpen),
   * а раз подсказка так и не открылась, закрытия, которое её вернуло бы, тоже нет — и все следующие всплывали мгновенно.
   * preventDefault на событии триггера — Radix пропускает свой обработчик.
   */
  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    triggerProps.onPointerMove?.(e);
    if (blocked(e.currentTarget)) e.preventDefault();
  };
  const onFocus = (e: FocusEvent<HTMLButtonElement>) => {
    triggerProps.onFocus?.(e);
    if (!lastInputWasKeyboard() || blocked(e.currentTarget)) e.preventDefault();
  };
  const root = (
    <T.Root open={open && !empty} onOpenChange={setOpen}>
      <T.Trigger {...triggerProps} onPointerMove={onPointerMove} onFocus={onFocus} ref={ref} asChild>
        {truncated ? cloneElement(children, undefined, (children.props as { children?: ReactNode }).children, <NoSafariTruncationTip />) : children}
      </T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={3}
          arrowPadding={12}
          collisionPadding={8}
          className={cn('nd-portal nd-tooltip nd:z-[1002]', TOOLTIP_SURFACE)}
        >
          {label}
          {/*
            Стрелка заходит под плашку на 2 px (высота 9, видно 7): вплотную при дробном масштабе страницы между ними
            проступала щель. Цвет тот же — шва не видно; у боковых сторон Radix поворачивает обёртку, сдвиг поворачивается с ней.
          */}
          <T.Arrow width={14} height={9} className="nd:-mt-0.5 nd:fill-primary" />
        </T.Content>
      </T.Portal>
    </T.Root>
  );
  // Вне раздела (тесты, отдельные истории) — свой счётчик: подсказка работает, только без «соседние сразу»
  return useContext(InsideProvider) ? root : <TooltipProvider>{root}</TooltipProvider>;
});
